/**
 * Opus for the browser, via WebCodecs (docs/protocol/audio-codec-v2.md).
 *
 * WebCodecs is asynchronous: frames go in, packets come out of a callback some
 * time later. That is fine here because the send path was already fire-and-
 * forget, but it means the encoder owns the frame's journey to the wire — the
 * caller hands over PCM and the callback sends.
 *
 * Everything degrades to null rather than throwing. A browser without
 * WebCodecs (Safari before 16.4, Firefox before 130, an old Android WebView)
 * must still be able to make a call, and it does: negotiation then settles on
 * mu-law for both sides.
 */

import { SAMPLE_RATE, FRAME_SAMPLES, OPUS_BITRATE } from './codecNegotiation.mjs';

const FRAME_US = Math.round((FRAME_SAMPLES / SAMPLE_RATE) * 1e6);  // 20000 µs

/** Encoder config. Shared with the support probe so we never advertise a config we cannot build. */
function encoderConfig() {
  return {
    codec: 'opus',
    sampleRate: SAMPLE_RATE,
    numberOfChannels: 1,
    bitrate: OPUS_BITRATE,
    bitrateMode: 'constant',
    opus: {
      frameDuration: FRAME_US,
      // DTX would let the encoder go quiet during silence. The padding makes
      // that invisible on the wire either way, but a continuous stream keeps
      // the far decoder's state warm, so we ask for it off.
      usedtx: false,
      application: 'voip',
    },
  };
}

function decoderConfig() {
  return { codec: 'opus', sampleRate: SAMPLE_RATE, numberOfChannels: 1 };
}

/**
 * Whether this browser can actually run the configuration we need — not just
 * whether the API exists. Chrome shipped AudioEncoder before it supported every
 * Opus knob, so the config is what gets asked about.
 */
export async function isOpusSupported() {
  if (typeof AudioEncoder === 'undefined' || typeof AudioDecoder === 'undefined') return false;
  try {
    const enc = await AudioEncoder.isConfigSupported(encoderConfig());
    if (!enc?.supported) return false;
    const dec = await AudioDecoder.isConfigSupported(decoderConfig());
    return !!dec?.supported;
  } catch (e) {
    return false;
  }
}

/**
 * Encoder. `onPacket(Uint8Array)` fires once per encoded 20 ms frame.
 * Returns null if the browser cannot do it.
 */
export function createOpusEncoder(onPacket, onError) {
  if (typeof AudioEncoder === 'undefined') return null;
  let timestamp = 0;
  let closed = false;

  let encoder;
  try {
    encoder = new AudioEncoder({
      output: (chunk) => {
        const buf = new Uint8Array(chunk.byteLength);
        chunk.copyTo(buf);
        onPacket(buf);
      },
      error: (e) => {
        // A dead encoder must not take the call with it: report once and let
        // the caller fall silent rather than throw on every 20 ms tick.
        closed = true;
        onError?.(e);
      },
    });
    encoder.configure(encoderConfig());
  } catch (e) {
    onError?.(e);
    return null;
  }

  return {
    /** @param {Float32Array} f32 one 20 ms mono frame */
    encode(f32) {
      if (closed || encoder.state !== 'configured') return;
      // Queue growth means the machine cannot keep up. Dropping the newest
      // frame is better than accumulating latency the user cannot recover from.
      if (encoder.encodeQueueSize > 4) return;
      try {
        const data = new AudioData({
          format: 'f32-planar',
          sampleRate: SAMPLE_RATE,
          numberOfFrames: f32.length,
          numberOfChannels: 1,
          timestamp,
          data: f32,
        });
        timestamp += FRAME_US;
        encoder.encode(data);
        data.close();
      } catch (e) {
        onError?.(e);
      }
    },
    /** Frames handed over but not yet encoded. Zero means the pipe is idle. */
    pending() {
      return closed ? 0 : encoder.encodeQueueSize;
    },
    close() {
      closed = true;
      try { if (encoder.state !== 'closed') encoder.close(); } catch (e) { /* already gone */ }
    },
  };
}

/**
 * Decoder. `onFrame(Float32Array)` fires once per decoded 20 ms frame.
 * Returns null if the browser cannot do it.
 */
export function createOpusDecoder(onFrame, onError) {
  if (typeof AudioDecoder === 'undefined') return null;
  let timestamp = 0;
  let closed = false;

  let decoder;
  try {
    decoder = new AudioDecoder({
      output: (audioData) => {
        const f32 = new Float32Array(audioData.numberOfFrames);
        audioData.copyTo(f32, { planeIndex: 0, format: 'f32-planar' });
        audioData.close();
        onFrame(f32);
      },
      error: (e) => { closed = true; onError?.(e); },
    });
    decoder.configure(decoderConfig());
  } catch (e) {
    onError?.(e);
    return null;
  }

  return {
    /** @param {Uint8Array} packet raw Opus, no container */
    decode(packet) {
      if (closed || decoder.state !== 'configured' || packet.length === 0) return;
      try {
        decoder.decode(new EncodedAudioChunk({
          type: 'key',   // every Opus packet stands alone
          timestamp,
          duration: FRAME_US,
          data: packet,
        }));
        timestamp += FRAME_US;
      } catch (e) {
        // One malformed packet costs one packet. It arrived over the network,
        // so it is exactly the input an attacker controls.
        onError?.(e);
      }
    },
    close() {
      closed = true;
      try { if (decoder.state !== 'closed') decoder.close(); } catch (e) { /* already gone */ }
    },
  };
}
